'use client'

import { Badge, Card, CardContent, CardHeader, CardTitle, ClipboardList } from '@chain-zen/ui'

import { AdditionalRequirements } from '../../../../../components/public-post/components/left-section/additional-requirements'
import { LabelRow } from '../../../../../components/shared/label-row'
import type { EquipmentTypeEnum } from '../../../../../types/equipment'
import type { IShipment } from '../../../../../types/shipment'
import { formatDistance, formatTemperature, formatWeight } from '../../../../../utils/common'

/**
 * Reference details component renders the details of a shipment's reference numbers
 * It displays the reference numbers and the equipment types
 */
export const ReferenceDetails = ({ shipment }: { shipment: IShipment }) => {
  if (!shipment || !shipment.equipment) return null

  const { commodity, distance, equipment, referenceNumbers, weight } = shipment

  const { equipmentLength, temperatureRange, isHazmat, equipmentTypes } = equipment || {}

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!!shipment.isShipmentOwner && (
          <>
            {!!referenceNumbers?.billOfLading && referenceNumbers?.billOfLading !== '' && (
              <LabelRow label="Bill of Lading">
                <p
                  className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto"
                  title={referenceNumbers?.billOfLading}
                >
                  {referenceNumbers?.billOfLading}
                </p>
              </LabelRow>
            )}
            {!!referenceNumbers?.billingReferenceNumber && referenceNumbers?.billingReferenceNumber !== '' && (
              <LabelRow label="Billing Reference Number">
                <p
                  className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto"
                  title={referenceNumbers?.billingReferenceNumber}
                >
                  {referenceNumbers?.billingReferenceNumber}
                </p>
              </LabelRow>
            )}
            {!!referenceNumbers?.externalTrackingID && referenceNumbers?.externalTrackingID !== '' && (
              <LabelRow label="External Tracking ID">
                <p
                  className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto"
                  title={referenceNumbers?.externalTrackingID}
                >
                  {referenceNumbers?.externalTrackingID}
                </p>
              </LabelRow>
            )}
            {!!referenceNumbers?.orderNumber && referenceNumbers?.orderNumber !== '' && (
              <LabelRow label="Order Number">
                <p
                  className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto"
                  title={referenceNumbers?.orderNumber}
                >
                  {referenceNumbers?.orderNumber}
                </p>
              </LabelRow>
            )}
            {!!referenceNumbers?.progressiveRotatingOrderNumber &&
              referenceNumbers?.progressiveRotatingOrderNumber !== '' && (
                <LabelRow label="Progressive Rotating Order Number">
                  <p
                    className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto"
                    title={referenceNumbers?.progressiveRotatingOrderNumber}
                  >
                    {referenceNumbers?.progressiveRotatingOrderNumber}
                  </p>
                </LabelRow>
              )}
            {!!referenceNumbers?.purchaseOrderNumber && referenceNumbers?.purchaseOrderNumber !== '' && (
              <LabelRow label="Purchase Order Number">
                <p
                  className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto"
                  title={referenceNumbers?.purchaseOrderNumber}
                >
                  {referenceNumbers?.purchaseOrderNumber}
                </p>
              </LabelRow>
            )}
          </>
        )}
        {!!equipmentTypes && equipmentTypes.length > 0 && (
          <LabelRow label="Equipment">
            <div className="flex flex-wrap gap-2 float-right">
              {equipmentTypes.map((truck: EquipmentTypeEnum) => (
                <Badge key={truck} variant="outline">
                  {truck}
                </Badge>
              ))}
            </div>
          </LabelRow>
        )}

        {!!commodity && (
          <LabelRow label="Commodity">
            <p className="text-sm text-muted-foreground text-right truncate max-w-[400px] ml-auto" title={commodity}>
              {commodity}
            </p>
          </LabelRow>
        )}

        {!!equipment?.additionalRequirements && equipment.additionalRequirements.length > 0 && (
          <AdditionalRequirements requirements={equipment.additionalRequirements} />
        )}

        {!!equipmentLength && !!equipmentLength.value && !!equipmentLength.unit && (
          <LabelRow label="Length">
            <p className="text-right text-sm">{`${equipmentLength.value} ${equipmentLength.unit}`}</p>
          </LabelRow>
        )}

        {!!temperatureRange && !!temperatureRange.min && !!temperatureRange.max && (
          <LabelRow label="Temperature">
            <div className="flex items-center gap-2 float-right">
              <p>❄️ </p>
              {formatTemperature({
                max: temperatureRange.max,
                min: temperatureRange.min,
                runType: temperatureRange.runType,
                unit: temperatureRange.unit
              })}
            </div>
          </LabelRow>
        )}

        {/* isTeam */}

        {!!weight && !!weight.value && !!weight.unit && (
          <LabelRow label="Weight">
            <p className="text-right text-sm">{formatWeight(weight.value, weight.unit)}</p>
          </LabelRow>
        )}

        {!!distance && !!distance.value && !!distance.unit && (
          <LabelRow label="Distance">
            <p className="text-right text-sm">{formatDistance(distance.value, distance.unit)}</p>
          </LabelRow>
        )}

        {!!isHazmat && (
          <LabelRow label="Hazmat">
            <p className="text-right text-sm">Yes</p>
          </LabelRow>
        )}
      </CardContent>
    </Card>
  )
}
